import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, ListChecks } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const statusStyles = {
  pending: "text-muted-foreground",
  sent: "text-[hsl(var(--success))]",
  edited: "text-[hsl(var(--warning))]",
  cancelled: "text-muted-foreground",
};

export default function History() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reviews");
        setReviews(data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="history-screen">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">History</p>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-1">Your reviews</h1>
      <p className="text-sm text-muted-foreground mb-8">Only you can see these.</p>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="history-loading">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center" data-testid="history-empty">
          <ListChecks className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-lg font-semibold mb-1">No reviews yet</p>
          <p className="text-sm text-muted-foreground mb-6">Run your first FINALLOOK to see it here.</p>
          <Link to="/app/compose">
            <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="history-empty-cta">
              Start a FINALLOOK <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="history-list">
          {reviews.map((r) => (
            <li key={r.id}>
              <Link
                to={`/app/reviews/${r.id}`}
                className="block rounded-xl border border-border bg-card px-4 py-4 hover:bg-secondary/40"
                data-testid={`history-item-${r.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm text-foreground line-clamp-2 flex-1">{r.message_preview}</p>
                  <span className="font-heading font-bold text-lg text-primary shrink-0 tabular-nums">{r.overall_score}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                  <span>·</span>
                  <span className={statusStyles[r.status] || ""}>{r.status}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
